import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import useAction from '../../../../prix/react/hooks/action'
import { useParams } from 'react-router-dom'
import { Answer, CitizenInfoProps, inputTypesAux } from '../questionnaireTypes'
import Loader from '~/prix/react/components/loader'
import citizenHealthProfileFetchByHashDefinition from '~/packages/publicHealth/citizenHealthProfile/fetchByHash/citizenHealthProfileFetchByHash.definition'
import { Button } from '~/prix/react/components/button'
import questionnaireSaveAnswersDefinition from '../sendAnswer/questionnaireSaveAnswers.definition'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import { toastError, toastSuccess } from '~/components/toasts'
import theme from '~/design'
import DateField from '~/prix/react/components/form/field/dateTimeField'
import format from 'date-fns/format'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  padding: 25px;
  gap: 20px;
  height: max-content;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    height: max-content;
  }
`

const QuestionaryAnswers = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  flex-direction: column;

  .question-options {
    width: 100%;

    div:has(:checked) {
      font-weight: bold;
      background-color: ${theme.colors.tertiary};
    }
  }

  .custom-select__control {
    height: 45px;
    border-radius: 8px;
    padding: 0.8rem 1rem;
    margin: 4px 0px 2px 0px;
  }

  .react-datepicker-wrapper {
    width: 40%;

    @media (max-width: 768px) {
      width: 100%;
    }

    input {
      width: 100%;
    }
  }
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 0px 32px 30px 0px rgba(20, 46, 82, 0.2);
  border: solid 1px rgba(221, 221, 221, 1);
  border-radius: 12px;
  margin: 0 16px;
  width: 25%;
  height: fit-content;

  .inner {
    margin: 20px;
    display: flex;
    flex-direction: column;
  }

  .info-container {
    display: flex;
    flex-direction: row;
    width: 100%;
    padding: 8px 4px;
    gap: 4px;
    align-items: center;

    .strong {
      font-weight: bold;
      font-size: 16px;
    }

    p {
      font-size: 14px;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 650px;
  }
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 0px 32px 30px 0px rgba(20, 46, 82, 0.2);
  border: solid 1px rgba(221, 221, 221, 1);
  border-radius: 12px;
  margin: 0 16px;
  width: 75%;
  height: fit-content;
  overflow: auto;

  .questionary {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 25px 30px 25px;
    align-items: center;
  }

  form {
    width: 100%;
  }

  .button-questionary {
    padding: 0 20px 25px 0;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    button {
      height: 35px;
      background-color: #0c2340;
    }
  }

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 650px;
    overflow: inherit;
    height: max-content;
  }
`

const TitleContainer = styled.div`
  border-radius: 12px 12px 0px 0px;
  background: #0c2340;
  height: fit-content;
  display: block;
  padding: 7px;

  p {
    margin-left: 1.7rem;
    color: #fff;
    font-size: 1.31034rem;
    font-weight: 700;
  }
`

const OptionContainer = styled.div<{ layout: string }>`
  display: flex;
  width: 100%;
  gap: 8px;
  padding: 8px 4px;
  border-radius: 4px;
  flex-direction: ${props => (props.layout === 'inverted' ? 'row' : 'column-reverse')};

  label {
    width: 100%;
  }
`

export default function CitizenQuestionnairesScreen() {
  const { hash } = useParams()
  const [answers, setAnswers] = useState<Answer[]>([])
  const questionsResult = useAction(
    citizenHealthProfileFetchByHashDefinition,
    { hash: hash || '' },
    [hash],
    {
      autoLoad: true,
    },
  )
  const { callAction: callQuestionnaireSaveAction } = useActionFunction(
    questionnaireSaveAnswersDefinition,
  )
  const [checkboxSelections, setCheckboxSelections] = useState<Record<number, number[]>>({})
  const [dateSelections, setDateSelections] = useState<Record<number, string | null>>({})
  const questions: CitizenInfoProps = questionsResult.result?.items[0]

  useEffect(() => {
    if (questions && !questionsResult.isLoading) {
      setAnswers([])
      questions.answers.map(answer => {
        setAnswers(prevAnswers => [
          ...prevAnswers,
          {
            questionId: answer.questionId,
            answerId: questions.questionnaires[0].id,
            string: answer.string,
            boolean: answer.boolean,
            numeric: answer.numeric,
            array: answer.array ? answer.array.map(Number) : null,
            date: answer.date,
          },
        ])

        if (answer.array && answer.array.length > 0) {
          setCheckboxSelections(prevSelections => {
            const newSelections = { ...prevSelections }
            newSelections[answer.questionId] = answer.array!.map(Number) || []
            return newSelections
          })
        }
      })
    }
  }, [questions, questionsResult.isLoading])

  const handleOptionSelect = ({
    questionId,
    answerSheetId,
    value,
  }: {
    questionId: number
    answerSheetId: number
    value: {
      string: string | null
      boolean: boolean | null
      numeric: number | null
      array: any[] | string[] | null
      date: string | null
    }
  }) => {
    setAnswers(prevAnswers => {
      const existingAnswerIndex = prevAnswers.findIndex(answer => answer.questionId === questionId)

      if (existingAnswerIndex !== -1) {
        const newAnswers = [...prevAnswers]
        newAnswers[existingAnswerIndex] = {
          ...newAnswers[existingAnswerIndex],
          answerId: answerSheetId,
          string: value.string ? value.string : null,
          boolean: value.boolean ? value.boolean : null,
          numeric: value.numeric ? value.numeric : null,
          array: value.array ? value.array : null,
          date: value.date ? value.date : null,
        }
        return newAnswers
      } else {
        return [
          ...prevAnswers,
          {
            questionId: questionId,
            answerId: answerSheetId,
            string: value.string ? value.string : null,
            boolean: value.boolean ? value.boolean : null,
            numeric: value.numeric ? value.numeric : null,
            array: value.array ? value.array : null,
            date: value.date ? value.date : null,
          },
        ]
      }
    })
  }

  const handleSave = async () => {
    try {
      const answerSheetId = hash || questions.hash
      const { resultPromise } = callQuestionnaireSaveAction({
        id: answerSheetId,
        originalLocalId: '',
        answers: answers.map(answer => ({
          ...answer,
          array: answer.array?.map(String) ?? null,
        })),
        citizenCns: null,
        citizenCpf: null,
      })

      resultPromise.then(() => {
        toastSuccess('Respostas salvas com sucesso')
      })
    } catch (error) {
      console.error(error)
      toastError('Erro ao salvar respostas')
    }
  }

  if (questionsResult.isLoading) {
    return <Loader />
  }

  if (questions?.questionnaires?.length === 0) {
    return (
      <Wrapper>
        <p>Não há questionários disponíveis no momento.</p>
      </Wrapper>
    )
  }

  if (!questions) {
    return (
      <Wrapper>
        <p>Erro ao carregar questionários</p>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <InfoWrapper>
        <TitleContainer>
          <p>Informações pessoais</p>
        </TitleContainer>
        <div className='inner'>
          <div className='info-container'>
            <p className='strong'>NOME:</p>
            <p>{questions.socialName || questions.name}</p>
          </div>
          <div className='info-container'>
            <p className='strong'>IDADE:</p>
            <p>{questions.age || 'Não informado'}</p>
          </div>
          <div className='info-container'>
            <p className='strong'>CNS:</p>
            <p>{questions.cns || 'Não informado'}</p>
          </div>
          <div className='info-container'>
            <p className='strong'>CPF:</p>
            <p>{questions.cpf || 'Não informado'}</p>
          </div>
        </div>
      </InfoWrapper>
      <FormWrapper>
        <TitleContainer>
          <p>FORMULÁRIO</p>
        </TitleContainer>
        {questions.questionnaires.map(questionnaire => (
          <div key={questionnaire.id} className='questionary'>
            <div>
              <h2>{questionnaire.title}</h2>
              {questionnaire.observation ? (
                <p>
                  <strong>Observações: </strong>
                  {questionnaire.observation}
                </p>
              ) : null}
              {questionnaire.lgpdDisclaimer ? (
                <p>
                  <strong>Uso de dados: </strong>
                  {questionnaire.lgpdDisclaimer}
                </p>
              ) : null}
            </div>
            <form>
              {Object.keys(inputTypesAux).map(type => {
                return questionnaire.questions
                  .filter(question => question.type === type)
                  .map(question => (
                    <QuestionaryAnswers key={question.id}>
                      <h3>{question.title}</h3>
                      <div className='question-options'>
                        {question.questionOptions.map((option, index) => (
                          <React.Fragment key={option.id}>
                            {question.type !== 'date' ? (
                              <OptionContainer
                                layout={
                                  ['optionButton', 'boolean', 'checkBox'].includes(question.type)
                                    ? 'inverted'
                                    : 'normal'
                                }
                              >
                                <input
                                  id={`option-${option.id}`}
                                  name={`question-${question.id}`}
                                  type={inputTypesAux[type as keyof typeof inputTypesAux]}
                                  //@todo: add value for other types of
                                  checked={
                                    checkboxSelections[question.id]?.includes(option.id) || false
                                  }
                                  onChange={e => {
                                    let newCheckboxSelections: any = []
                                    if ('checkBox' === question.type) {
                                      setCheckboxSelections(prevSelections => {
                                        const newSelections = { ...prevSelections }
                                        if (e.target.checked) {
                                          if (!newSelections[question.id]) {
                                            newSelections[question.id] = []
                                          }
                                          newSelections[question.id].push(option.id)
                                        } else {
                                          newSelections[question.id] = newSelections[
                                            question.id
                                          ].filter(id => id !== option.id)
                                        }
                                        newCheckboxSelections = newSelections
                                        return newSelections
                                      })
                                    }

                                    if ('optionButton' === question.type) {
                                      setCheckboxSelections(prevSelections => {
                                        const newSelections = { ...prevSelections }
                                        if (e.target.checked) {
                                          newSelections[question.id] = [option.id]
                                        } else {
                                          newSelections[question.id] = []
                                        }
                                        return newSelections
                                      })
                                    }

                                    setTimeout(() => {
                                      handleOptionSelect({
                                        questionId: question.id,
                                        answerSheetId: questionnaire.id,
                                        value: {
                                          string: null,
                                          boolean:
                                            question.type === 'boolean' ? e.target.checked : null,
                                          numeric:
                                            question.type === 'numeric'
                                              ? Number(e.target.value)
                                              : null,
                                          array:
                                            question.type === 'optionButton'
                                              ? [option.id]
                                              : question.type === 'checkBox'
                                              ? newCheckboxSelections[question.id]
                                              : null,
                                          date: null,
                                        },
                                      })
                                    }, 0)
                                  }}
                                />
                                <label htmlFor={`option-${option.id}`}>{option.label}</label>
                              </OptionContainer>
                            ) : (
                              <DateField
                                key={`question-${question.id}`}
                                placeholder='Selecione uma data'
                                value={dateSelections[question.id]}
                                onDirectChange={date => {
                                  handleOptionSelect({
                                    questionId: question.id,
                                    answerSheetId: questionnaire.id,
                                    value: {
                                      string: null,
                                      boolean: null,
                                      numeric: null,
                                      array: null,
                                      date: date ? format(date, 'yyyy-MM-dd') : null,
                                    },
                                  })
                                }}
                              />
                            )}
                            {index !== question.questionOptions.length - 1 ? <hr /> : null}
                          </React.Fragment>
                        ))}
                      </div>
                    </QuestionaryAnswers>
                  ))
              })}
            </form>
          </div>
        ))}
        <div className='button-questionary'>
          <Button onClick={handleSave}>Salvar respostas</Button>
        </div>
      </FormWrapper>
    </Wrapper>
  )
}
